import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const DRIVER_OPTIONS = [
  { count: 1, label: '1 Driver', desc: 'Small lunch rush or single-route delivery' },
  { count: 3, label: '3 Drivers', desc: 'Moderate volume — dinner service or catering' },
  { count: 5, label: '5 Drivers', desc: 'High volume event or peak service day' },
];

const DURATION_OPTIONS = [
  { key: 'daily', label: 'Daily', desc: 'Today only' },
  { key: 'weekly', label: 'Weekly', desc: '7-day schedule' },
  { key: 'monthly', label: 'Monthly', desc: 'Full month commitment' },
];

export default function OrderDrivers() {
  const navigate = useNavigate();
  const [driverCount, setDriverCount] = useState(1);
  const [duration, setDuration] = useState('daily');
  const [submitted, setSubmitted] = useState(false);

  // Estimated cost calculation
  const deliveriesPerDriverPerDay = 6;
  const deliveryFee = 8; // starter rate for estimate
  const driverGuarantee = 5;

  const days = duration === 'daily' ? 1 : duration === 'weekly' ? 7 : 30;
  const estimatedDeliveries = driverCount * deliveriesPerDriverPerDay * days;
  const estimatedRevenue = estimatedDeliveries * deliveryFee;
  const driverPay = estimatedDeliveries * driverGuarantee;
  const yourCost = estimatedDeliveries * deliveryFee;

  function handleSubmit() {
    setSubmitted(true);
    setTimeout(() => navigate('/create-delivery'), 2000);
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-6xl mb-4">🚗</div>
          <h2 className="text-3xl font-bold text-white mb-2">Drivers requested!</h2>
          <p className="text-gray-400 mb-6">
            We're matching you with {driverCount} available driver{driverCount > 1 ? 's' : ''} in your area.
            You'll receive a text when they're assigned.
          </p>
          <p className="text-gray-500 text-sm">Redirecting to create your first delivery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center gap-4">
        <Link to="/dashboard" className="text-gray-400 hover:text-white transition">← Back</Link>
        <h1 className="text-xl font-bold">Request Drivers</h1>
      </header>

      <main className="px-4 md:px-8 py-8 max-w-3xl mx-auto">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">How many drivers do you need?</h2>
          <p className="text-gray-400">We'll match you with available drivers in your delivery zone.</p>
        </div>

        {/* Driver count selection */}
        <section className="mb-8">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-4">Number of Drivers</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {DRIVER_OPTIONS.map((opt) => (
              <button
                key={opt.count}
                onClick={() => setDriverCount(opt.count)}
                className={`
                  p-5 rounded-2xl border text-left transition-all
                  ${driverCount === opt.count
                    ? 'border-yellow-400 bg-yellow-400/10'
                    : 'border-gray-700 bg-gray-900 hover:border-gray-500'
                  }
                `}
              >
                <div className="text-4xl font-black text-yellow-400 mb-2"
                  style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
                  {opt.label}
                </div>
                <div className="text-sm text-gray-400">{opt.desc}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Duration selection */}
        <section className="mb-8">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-4">Duration</h3>
          <div className="flex gap-3">
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setDuration(opt.key)}
                className={`
                  flex-1 p-4 rounded-2xl border text-center transition-all
                  ${duration === opt.key
                    ? 'border-yellow-400 bg-yellow-400/10'
                    : 'border-gray-700 bg-gray-900 hover:border-gray-500'
                  }
                `}
              >
                <div className="font-semibold text-white">{opt.label}</div>
                <div className="text-xs text-gray-400 mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Estimate summary */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-8">
          <h3 className="font-semibold text-white mb-4">Estimated Impact</h3>
          <div className="space-y-3">
            {[
              { label: 'Estimated deliveries', value: `~${estimatedDeliveries}` },
              { label: 'Delivery fee (Starter rate)', value: `$${deliveryFee}/delivery` },
              { label: 'Driver guaranteed minimum', value: `$${driverGuarantee}/delivery` },
              { label: 'Your estimated cost', value: `$${yourCost}`, highlight: true },
              { label: 'Driver estimated earnings', value: `$${driverPay}`, highlight: true },
            ].map((row) => (
              <div key={row.label} className="flex justify-between items-center">
                <span className="text-sm text-gray-400">{row.label}</span>
                <span className={`text-sm font-semibold ${row.highlight ? 'text-yellow-400' : 'text-white'}`}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-600 mt-4">
            * Estimates based on ~6 deliveries per driver per day. Upgrade to Business or Enterprise for lower per-delivery rates.
          </p>
        </div>

        <button
          onClick={handleSubmit}
          className="w-full py-4 bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-lg rounded-2xl transition-all active:scale-95"
        >
          Request {driverCount} Driver{driverCount > 1 ? 's' : ''} →
        </button>
      </main>
    </div>
  );
}
